import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { CARD_BREAKPOINTS, CARD_MIN_WIDTHS } from '~/constants/cardBreakpoints';
import { useScrollableCards } from '~/hooks/useScrollableCards';

export function ScrollableCardsGrid({ children }: { children: ReactNode }) {
  const { scrollRef, showLeft, showRight, pages, scroll } = useScrollableCards({
    breakpoints: {
      mobile: CARD_BREAKPOINTS.mobile,
      tablet: CARD_BREAKPOINTS.tablet,
      desktop: CARD_BREAKPOINTS.desktop,
      large: CARD_BREAKPOINTS.large,
    },
    children,
  });

  return (
    <ScrollContainer>
      <NavButton
        $direction="left"
        $visible={showLeft}
        onClick={() => scroll('left')}
        // Translation Needed
        aria-label="Scroll left"
      >
        <IconWrapper $direction="left">
          <FontAwesomeIcon icon={faChevronLeft} />
        </IconWrapper>
      </NavButton>

      <ScrollWrapper ref={scrollRef}>
        <Grid>
          {pages.map((pageCards, pageIndex) => (
            <CardPage key={pageIndex}>{pageCards}</CardPage>
          ))}
          <EndSpacer />
        </Grid>
      </ScrollWrapper>

      <NavButton
        $direction="right"
        $visible={showRight}
        onClick={() => scroll('right')}
        // Translation Needed
        aria-label="Scroll right"
      >
        <IconWrapper $direction="right">
          <FontAwesomeIcon icon={faChevronRight} />
        </IconWrapper>
      </NavButton>
    </ScrollContainer>
  );
}

const CardPage = styled.div`
  box-sizing: border-box;
  display: grid;
  flex-shrink: 0;
  gap: 26px;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  width: 100%;
  @media (max-width: ${CARD_BREAKPOINTS.mobile}px) {
    gap: 8px;
    justify-items: center;
    padding: 0;
    & > * {
      max-width: 260px;
      width: 100%;
    }
  }
  @media (min-width: ${CARD_BREAKPOINTS.large}px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (max-width: ${CARD_BREAKPOINTS.desktop}px) and (min-width: ${CARD_MIN_WIDTHS.tablet}px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: ${CARD_BREAKPOINTS.tablet}px) and (min-width: ${CARD_MIN_WIDTHS.mobile}px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${CARD_BREAKPOINTS.mobile}px) {
    grid-template-columns: 1fr;
  }
`;

const EndSpacer = styled.div`
  flex-shrink: 0;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  width: 1px;
`;

const Grid = styled.div`
  box-sizing: border-box;
  display: flex;
  gap: 58px;
  width: 100%;
  @media (max-width: ${CARD_BREAKPOINTS.mobile}px) {
    gap: 16px;
  }
`;

const NavButton = styled.button<{
  $direction: 'left' | 'right';
  $visible: boolean;
}>`
  background: none;
  border: none;
  cursor: pointer;
  height: 80px;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  padding: 0;
  pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 0.3s ease;
  width: 40px;
  z-index: 10;

  ${(props) => (props.$direction === 'left' ? 'left: 0;' : 'right: 0;')}

  &::before {
    background: var(--grey-300);
    border-radius: ${(props) =>
      props.$direction === 'left' ? '0 40px 40px 0' : '40px 0 0 40px'};
    content: '';
    height: 80px;
    position: absolute;
    top: 0;
    transform: translateX(
      ${(props) => (props.$direction === 'left' ? '-50%' : '50%')}
    );
    transition: transform 0.3s ease;
    width: 40px;
    ${(props) => (props.$direction === 'left' ? 'left: 0;' : 'right: 0;')}
  }

  &:hover::before {
    transform: translateX(0);
  }

  svg {
    color: var(--grey-700);
    font-size: 18px;
  }
`;

const IconWrapper = styled.div<{ $direction: 'left' | 'right' }>`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: ${(props) =>
    props.$direction === 'left'
      ? 'translate(-150%, -50%)'
      : 'translate(50%, -50%)'};
  transition: transform 0.3s ease;
  width: 100%;
  z-index: 2;

  ${NavButton}:hover & {
    transform: translate(-50%, -50%);
  }
`;

const ScrollContainer = styled.div`
  background: var(--white);
  box-shadow: var(--shadow-inset-lg);
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  width: 100%;
  @media (max-width: ${CARD_BREAKPOINTS.mobile}px) {
    border-radius: 0;
  }
`;

const ScrollWrapper = styled.div`
  box-sizing: border-box;
  -ms-overflow-style: none;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 15px 55px;
  scroll-behavior: smooth;
  scroll-padding: 0 55px;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  width: 100%;
  @media (max-width: ${CARD_BREAKPOINTS.mobile}px) {
    padding: 8px 8px;
    scroll-padding: 0 8px;
  }
  &::-webkit-scrollbar {
    display: none;
  }
`;
